import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Building2, FileText, ClipboardCheck, CalendarDays,
  ShieldCheck, BookOpen, Target, Loader2, Command,
} from "lucide-react";
import api from "@/api/client";

const ICONS = {
  client: Building2,
  proposal: Target,
  visit: CalendarDays,
  document: FileText,
  action: ClipboardCheck,
  obligation: ShieldCheck,
  library: BookOpen,
};

const LABELS = {
  client: "Cliente",
  proposal: "Proposta",
  visit: "Visita",
  document: "Documento",
  action: "Ação",
  obligation: "Obrigação",
  library: "Biblioteca",
};

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (event.key === "Escape") setOpen(false);
    };
    const onPointer = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, []);

  React.useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setItems([]);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/global-search", { params: { q: term, limit_per_type: 5 } });
        if (active) setItems(data?.items || []);
      } catch {
        if (active) setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 220);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const choose = (item) => {
    setOpen(false);
    setQuery("");
    setItems([]);
    navigate(item.href);
  };

  return (
    <div ref={containerRef} className="relative hidden md:block" data-testid="global-search">
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="flex h-10 w-[220px] items-center gap-2 rounded-xl border border-border/70 bg-card/60 px-3 text-left text-sm text-muted-foreground transition-colors hover:border-primary/30 hover:bg-accent lg:w-[300px]"
        aria-label="Abrir busca global"
      >
        <Search className="size-4" />
        <span className="min-w-0 flex-1 truncate">Buscar no CEO...</span>
        <span className="hidden items-center gap-0.5 rounded-md border border-border/70 bg-background/70 px-1.5 py-0.5 text-[10px] font-medium lg:flex">
          <Command className="size-2.5" />K
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-[min(620px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border/80 bg-popover shadow-2xl">
          <div className="flex items-center gap-3 border-b border-border/70 px-4 py-3">
            <Search className="size-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Cliente, CNPJ, proposta, visita, documento, ação..."
              className="h-8 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              aria-label="Busca global"
            />
            {loading && <Loader2 className="size-4 animate-spin text-primary" />}
          </div>

          <div className="max-h-[460px] overflow-y-auto p-2">
            {query.trim().length < 2 && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                Digite pelo menos 2 caracteres para buscar nos registros operacionais.
              </div>
            )}
            {query.trim().length >= 2 && !loading && items.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">Nenhum resultado encontrado.</div>
            )}
            {items.map((item) => {
              const Icon = ICONS[item.type] || Search;
              return (
                <button
                  key={`${item.type}-${item.id}`}
                  type="button"
                  onClick={() => choose(item)}
                  className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent"
                >
                  <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg border border-border/70 bg-background/70 text-primary">
                    <Icon className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">{item.title}</span>
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{LABELS[item.type] || item.type}</span>
                    </span>
                    {item.subtitle && <span className="mt-0.5 block truncate text-xs text-muted-foreground">{item.subtitle}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
